import { useMutation, useQuery } from '@apollo/client';
import { Info } from '@clearkit/icons';
import { CKBannerNext, CKButton, CKModal } from 'clearkit';
import { uniq } from 'lodash';
import { FC, useState } from 'react';

import MultiPicklist from '~/components/MultiPicklist';
import { ProductFeatureEnum, Segment } from '~/generated/graphql';
import { trackCSVExported } from '~/lib/segmentAnalytics';

import csvExportQuery from './csvExportQuery.graphql';
import exportCsvMutation from './mutation.graphql';

interface CsvExportDialogProps {
  open: boolean;
  onClose: () => void;
  searchQuery?: string;
  segment?: Segment;
  type: 'Company' | 'Person';
}

const CsvExportDialog: FC<CsvExportDialogProps> = ({
  open,
  onClose,
  searchQuery,
  segment,
  type,
}) => {
  const [columns, setColumns] = useState<string[] | undefined>(undefined);
  const [step, setStep] = useState<'columns' | 'confirmation'>('columns');

  const { data, loading, error } = useQuery(csvExportQuery, {
    variables: {
      attributeType: type,
      columnType: type,
      ...(segment?.id && { segmentId: segment.id }),
    },
  });

  const [exportCsv] = useMutation(exportCsvMutation);

  if (loading || error) {
    return null;
  }

  const columnObjects = (allColumns: any[], stringColumns: string[]) => {
    return stringColumns.map((stringColumn) => {
      const fullColumn = allColumns.find(
        (fullColumn) => fullColumn.value === stringColumn,
      );
      return { ...fullColumn };
    });
  };

  const handleCompleted = (segment: any, columns: any) => {
    trackCSVExported(
      segment,
      uniq(columns.map((column: any) => column.category)),
    );
    setStep('confirmation');
  };

  const {
    columns: dataColumns,
    attributes,
    viewer: {
      user: { email },
    },
    productFeatures,
  } = data;

  const columnObjectsData = columnObjects(attributes, dataColumns);

  const shouldShowUsageWarning = (productFeatures || []).some(
    (feature: any) => ProductFeatureEnum.StandardBundle === feature.id,
  );

  const emptyWarning =
    "You can't export an empty CSV. Add some attributes from the list to the left.";

  return (
    <CKModal
      isVisible={open}
      onToggle={onClose}
      size={step === 'columns' ? 'xl' : 'md'}
    >
      {step === 'columns' ? (
        <>
          <CKModal.Header>
            <CKModal.Heading>Configure the CSV file to export</CKModal.Heading>
          </CKModal.Header>
          <CKModal.Body className="px-8 py-4">
            By default Clearbit includes a standard set of data attributes in
            the CSV export for this audience. You can customize the attributes
            that are included and specify your own below.
            {shouldShowUsageWarning ? (
              <CKBannerNext className="mt-2" variant="warning">
                <CKBannerNext.IconStatus>
                  <Info className="fill-gradient-br-yellow" />
                </CKBannerNext.IconStatus>
                <CKBannerNext.Header className="text-left">
                  Warning
                </CKBannerNext.Header>
                <CKBannerNext.Body className="text-left">
                  Exporting an audience here will cost API credits. Please
                  double check your audience size
                </CKBannerNext.Body>
              </CKBannerNext>
            ) : null}
            <MultiPicklist
              allColumns={attributes}
              emptyWarning={emptyWarning}
              hubspotComingSoon
              onChange={(columns: any) => {
                setColumns(columns);
              }}
              type={type}
              value={columnObjectsData}
            />
          </CKModal.Body>
          <CKModal.Footer className="flex gap-4">
            <CKButton onClick={onClose} variant="simple">
              Dismiss
            </CKButton>
            <CKButton
              isDisabled={(columns?.length || 0) === 0}
              onClick={() =>
                exportCsv({
                  variables: {
                    input: {
                      columns,
                      searchQuery,
                      type,
                      ...(segment?.id && { segmentId: segment.id }),
                    },
                  },
                }).then(() => handleCompleted(segment, columns))
              }
              variant="bold"
              variantColor="blue"
            >
              Begin export
            </CKButton>
          </CKModal.Footer>
        </>
      ) : null}
      {step === 'confirmation' ? (
        <>
          <CKModal.Header>
            <CKModal.Heading>
              Your CSV download is being prepared
            </CKModal.Heading>
          </CKModal.Header>
          <CKModal.Body className="px-8 py-4">
            We&apos;ll email you at the address below when your CSV is ready for
            download.
            <p>{email}</p>
          </CKModal.Body>
          <CKModal.Footer>
            <CKButton onClick={onClose} variant="simple">
              Dismiss
            </CKButton>
          </CKModal.Footer>
        </>
      ) : null}
    </CKModal>
  );
};

export default CsvExportDialog;
